<template>
  <v-card
    class="elevation-3 fade-in-up bg-dot-pattern"
    light
  >
    <v-card-text>
      <div class="mb-6 mt-2 text-center">
        <h1 class="text-h4 font-weight-bold blue--text text--darken-4 mb-2" style="min-height: 40px;">
          {{ typedText }}<span class="cursor" :class="{ 'blinking': !isTyping }">|</span>
        </h1>
      </div>

      <content-section title="Current Focus">
        <p>
          I’m excited to announce my new role as a <strong>Customer Success Engineer at IDEMIA Public Security</strong>! I am currently focused on guiding clients through seamless API and SDK integrations for AWS SaaS Identity Verification and Digital ID solutions. I'm looking forward to leveraging my background in technical support and cloud infrastructure to ensure our clients' success.
        </p>
      </content-section>

      <div class="scroll-fade">
        <content-section title="Who am I really?">
          <p><strong>Global Support Leader:</strong> "With over 20 years in technical support leadership, I don't just manage queues—I build scalable, global ecosystems. I believe in servant leadership, empowering teams across continents to perform at their best while using data to drive strategic decisions."</p>
          <p><strong>Technical Innovator:</strong> "I bridge the gap between Support and Engineering. My background in Computer Science allows me to build automation tools and scripts that reduce toil, optimize workflows, and solve complex technical problems that standard support teams can't."</p>
          <p><strong>Veteran:</strong> "Serving in the United States Army instilled unshakeable resilience and a duty to service. It taught me how to make critical decisions under pressure and how to foster discipline and camaraderie within a diverse unit."</p>
          <p><strong>Father & Mentor:</strong> "Whether guiding my own children through college or mentoring junior engineers, I strive to create environments where people feel safe to learn, fail, and ultimately thrive. I measure my success by the growth of those around me."</p>
        </content-section>
      </div>

      <div class="scroll-fade">
        <content-section title="Professional Experience">
          <v-timeline dense align-top>
            <v-timeline-item
              v-for="(experience, i) in experiences"
              :key="i"
              :color="experience.color"
              :icon="experience.icon"
              fill-dot
              small
            >
              <v-card class="elevation-2 mb-3">
                <v-card-title class="pb-1 pt-3">
                  <strong :class="experience.textColor">{{ experience.role }}</strong>
                </v-card-title>
                <v-card-subtitle class="pb-2 grey--text text--darken-3 font-weight-medium">
                  {{ experience.company }} <span v-if="experience.date">| {{ experience.date }}</span>
                </v-card-subtitle>
                <v-card-text class="pt-0 text-body-1">
                  {{ experience.description }}
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
        </content-section>
      </div>

      <div v-if="prouds && prouds.length" class="scroll-fade">
        <content-section title="What am I proud of?">
          <v-layout
            v-for="(proud, i) in prouds"
            :key="i"
            class="mb-3"
          >
            <v-flex md1 xs3>
              <v-icon right color="blue darken-4" large>
                {{ proud.icon }}
              </v-icon>
            </v-flex>
            <v-flex md11 xs9>
              <p class="mb-0">
                <strong class="blue--text text--darken-4">{{ proud.text }}</strong><br>
                <small class="grey--text text--darken-1">{{ proud.source }}</small>
              </p>
            </v-flex>
          </v-layout>
        </content-section>
      </div>

      <div v-if="educations && educations.length" class="scroll-fade">
        <content-section title="Education">
          <v-layout
            v-for="(education, i) in educations"
            :key="i"
            class="mb-4"
          >
            <v-flex md4 class="font-weight-bold red--text text--darken-2">
              {{ education.from }} - {{ education.to }}
            </v-flex>
            <v-flex md8>
              <strong v-if="education.title" class="blue--text text--darken-4">{{ education.title }}</strong>
              <div>
                <strong v-if="education.name">{{ education.name }}</strong>
              </div>
              <div v-if="education.location" class="grey--text text--darken-2">
                <i>{{ education.location }}</i>
              </div>
              <div v-if="education.description" class="mt-1">
                {{ education.description }}
              </div>
            </v-flex>
          </v-layout>
        </content-section>
      </div>

      <div v-if="certificates && certificates.length" class="scroll-fade">
        <content-section title="Certificates">
          <v-layout
            v-for="(certificate, i) in certificates"
            :key="i"
            class="mb-3"
          >
            <v-flex md12>
              <strong v-if="certificate.title" class="blue--text text--darken-4">{{ certificate.title }}</strong>
              <div>
                <strong v-if="certificate.name">{{ certificate.name }}</strong>
              </div>
              <div>
                <a 
                  v-if="certificate.link"
                  :href="certificate.link"
                  target="_blank"
                  class="red--text text--darken-2 text-decoration-none"
                >
                  {{ certificate.location }}
                </a>
                <span v-else class="grey--text">
                  {{ certificate.location }}
                </span>
              </div>
              <div v-if="certificate.description" class="mt-1">
                {{ certificate.description }}
              </div>
            </v-flex>
          </v-layout>
        </content-section>
      </div>

      <div v-if="skills && skills.length" class="scroll-fade">
        <content-section id="to-timeline" title="Skills">
          <template slot="actions">
            <span class="grey--text caption">(% are relative not absolute)</span>
          </template>
          <v-layout wrap>
            <template v-for="(skill, i) in skills">
              <v-flex
                v-if="skill.divider"
                :key="i"
                md12
                xs12
                mb-4
              />
              <v-flex
                v-else
                :key="'skill-'+i"
                md6
                xs12
              >
                <div class="mr-3 ml-3 mb-3">
                  <div class="align-center pb-1">
                    <v-icon small color="blue darken-4" class="mr-1">
                      {{ skill.icon }}
                    </v-icon>
                    <strong class="blue--text text--darken-4">{{ skill.title }}</strong>
                  </div>
                  <v-progress-linear
                    class="progress elevation-1"
                    color="red darken-2"
                    height="6"
                    rounded
                    :value="animateSkills ? skill.value : 0"
                  />
                </div>
              </v-flex>
            </template>
          </v-layout>
        </content-section>
      </div>

    </v-card-text>
  </v-card>
</template>

<script>
import ContentSection from '@/views/dark-template/content/Section'

export default {
  name      : 'MainContent',
  components: { ContentSection },
  data      : () => ({
    animateSkills: false, 
    fullIntroText: "Joseph D. Jordan // Customer Success & Technical Support Leader",
    typedText: "",
    isTyping: true,
    experiences: [
      {
        role: 'Customer Success Engineer',
        company: 'IDEMIA Public Security',
        date: 'Present',
        color: 'red darken-2',
        textColor: 'red--text text--darken-2',
        icon: 'mdi-shield-account',
        description: 'Guiding clients through seamless API and SDK integrations for AWS SaaS Identity Verification and Digital ID solutions. Leveraging background in technical support and cloud infrastructure to ensure long-term client success and adoption.'
      },
      {
        role: 'Head of Customer Support',
        company: 'Sycurio',
        date: '2022 - 2025', 
        color: 'blue darken-4', 
        textColor: 'blue--text text--darken-4',
        icon: 'mdi-headset',
        description: 'Built and scaled global support ecosystems, managing teams across continents. Optimized operational workflows using data-driven strategies, and reduced time-to-resolution by bridging the gap between Support and Engineering.'
      },
      {
        role: 'Soldier / Non-Commissioned Officer',
        company: 'United States Army',
        date: '1998 - 2013', 
        color: 'red darken-2',
        textColor: 'red--text text--darken-2',
        icon: 'mdi-star',
        description: 'Developed unshakeable resilience and leadership skills under pressure. Fostered discipline, camaraderie, and operational readiness within a highly diverse unit while carrying out critical mission objectives.'
      }
    ],
    prouds: [
      { icon: 'mdi-heart', text: 'My family' },
      { icon: 'mdi-flag', text: 'Having the honor to serve in the United States Army' },
      { icon: 'mdi-teach', text: 'Being an Adjunct Instructor and helping others learn to code' },
      { icon: 'mdi-airplane', text: 'Having the opportunity to travel' },
    ],
    educations: [
      {
        from       : 'August 2013',
        to         : 'December 2016',
        title      : 'Bachelor of Science in Computer Science',
        name       : 'Lamar University',
        location   : 'Texas',
        description: 'Concentration in Game Design and a Minor in Business Management',
      },
    ],
    certificates: [
      {
        title      : 'Project Planning: Putting It All Together',
        location   : 'Coursera.org',
        link       : 'https://coursera.org/verify/YKQH3BLVSUSY',
        name       : 'Google Career Certificates',
        description: 'Examine key components of the project plan, create milestones, discover tools, and evaluate risk',
      },
      {
        title      : 'Project Initiation: Starting a Successful Project',
        location   : 'Coursera.org',
        link       : 'https://coursera.org/verify/5677KGWQPYUB',
        name       : 'Google Career Certificates',
        description: 'Understand the significance of the initiation phase of the project life cycle and the key components',
      },
      {
        title      : 'Foundations of Project Management',
        location   : 'Coursera.org',
        link       : 'https://coursera.org/verify/AVQDZGAKQKXB',
        name       : 'Google Career Certificates',
        description: 'Foundations of Project Management, the skills, roles, and responsibilities across a variety of industries',
      },
      {
        title      : 'Technical Support Fundamentals',
        location   : 'Coursera.org',
        link       : 'http://coursera.org/verify/MJC2CH6FDS92',
        name       : 'Google Career Certificates',
        description: 'Introduction into Information Technology, Hardware, Operating Systems, Networking, Software, and Troubleshooting',
      },
      {
        title      : 'The Bits and Bytes of Computer Networking',
        location   : 'Coursera.org',
        link       : 'https://coursera.org/verify/ZBJNMVCMYDPR',
        name       : 'Google Career Certificates',
        description: 'Introduction to Networking, The Network Layer, The Transport and Application Layers, Networking Services, Connecting to the Internet, and Troubleshooting and the Future of Networking',
      },
      {
        title      : 'CompTIA Security+',
        location   : 'CompTIA',
        link       : 'https://www.comptia.org/',
        name       : 'CompTIA',
        description: 'Certified that the user has the knowledge and skills required to assess the security posture of an enterprise environment, recommend and implement appropriate security solutions, monitor and secure hybrid environments, operate with an awareness of applicable laws and policies, and identify, analyze, and respond to security events and incidents.',
      },
    ],
    skills: [
      { title: 'Global Team Leadership', icon : 'mdi-account-group', value: 100 },
      { title: 'Strategic Planning', icon : 'mdi-chess-knight', value: 90 },
      { title: 'Crisis Management', icon : 'mdi-alert-circle', value: 95 },
      { title: 'Process Optimization', icon : 'mdi-chart-line', value: 100 },
      { divider: true },
      { title: 'Workflow Automation', icon : 'mdi-robot', value: 100 },
      { title: 'Jira & Incident Management', icon : 'mdi-jira', value: 100 },
      { title: 'SQL & Data Reporting', icon : 'mdi-database', value: 95 },
      { title: 'Web App Security', icon : 'mdi-shield-lock', value: 90 },
      { divider: true },
      { title: 'Linux / Server Admin', icon : 'mdi-linux', value: 95 },
      { title: 'Python & Scripting', icon : 'mdi-language-python', value: 90 },
      { title: 'Git & Version Control', icon : 'mdi-git', value: 95 },
      { title: 'C# / C++ / Java', icon : 'mdi-code-braces', value: 80 },
    ],
  }),

  mounted() {
    this.startTyping();
    
    // Trigger the skill bars to fill up 1.5 seconds after the page loads
    setTimeout(() => {
      this.animateSkills = true;
    }, 1500);

    // Native JavaScript Intersection Observer (Works in all Vue versions)
    this.$nextTick(() => {
      const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Unobserve so it doesn't repeat the animation if they scroll back up
            observerInstance.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1 // Triggers when 10% of the element comes into view
      });

      // Find all elements with the 'scroll-fade' class and attach the observer
      const elements = this.$el.querySelectorAll('.scroll-fade');
      elements.forEach(el => observer.observe(el));
    });
  },

  methods: {
    startTyping() {
      let i = 0;
      const speed = 85; 

      const typeWriter = () => {
        if (i < this.fullIntroText.length) {
          this.typedText += this.fullIntroText.charAt(i);
          i++;
          const randomSpeed = speed + (Math.random() * 40 - 20); 
          setTimeout(typeWriter, randomSpeed);
        } else {
          this.isTyping = false; 
        }
      };

      setTimeout(typeWriter, 1200); 
    }
  }
}
</script>

<style scoped>
/* 1. The Engineering Dot Pattern */
.bg-dot-pattern {
  background-color: #ffffff !important;
  /* Inline SVG for a perfect, resolution-independent dot */
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='3' cy='3' r='1.5' fill='%231565c0' fill-opacity='0.15'/%3E%3C/svg%3E") !important;
  /* Forces Vuetify to tile the pattern instead of showing just one */
  background-repeat: repeat !important; 
}

/* 2. Initial Page Load Animation */
.fade-in-up {
  animation: fadeInUp 1s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 3. Scroll-Triggered Animation Classes */
.scroll-fade {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.scroll-fade.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.title {
  border-bottom: 2px #d32f2f solid;
  line-height: 1.5 !important;
}

.progress {
  margin-top: 0.2rem;
}

.cursor {
  font-weight: 300;
  color: #d32f2f;
  margin-left: 2px;
}

.cursor.blinking {
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
/* --- FIX FOR BLOCKED BACKGROUND DOTS --- */
/* Force the content sections to be transparent so dots show through */
::v-deep .v-card__text > div {
  background-color: transparent !important;
}
::v-deep .v-card__text > .scroll-fade {
  background-color: transparent !important;
}

/* Ensure the timeline cards STAY solid white so the text remains readable */
::v-deep .v-timeline-item .v-card {
  background-color: #ffffff !important;
}
</style>